import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaseComponent } from 'src/app/base/base.component';
import { FinancialService } from 'src/app/service/financial.service';
import { Criteria } from '../../model/scoringCriteria';
import { GetRemunerationParametersGQL } from '../../../generated/graphql/remunerationParameters/get-remunerationParameters.graphql'; 
import { GetScoreLevelsGQL } from '../../../generated/graphql/scoreLevel/get-scoreLevel.graphql';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-financial',
  templateUrl: './financial.component.html',
  styleUrls: ['./financial.component.scss'],
})
export class FinancialComponent extends BaseComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  criteriaList: Criteria[] = [];
  fullCriteriaList: Criteria[] = [];
  selectedCriteria: Criteria = new Criteria();
  remunerations: { id: number; name: string }[] = [];
  scoreLevels: { id: number; levelName: string }[] = [];
  searchText: string = '';

  constructor(
    public translate: TranslateService,
    public loading: NgxSpinnerService,
    public router: Router,
    private financialService: FinancialService,
    private getRemunerationParametersGQL: GetRemunerationParametersGQL,
    private getScoreLevelsGQL: GetScoreLevelsGQL
  ) {
    super(router, loading, translate);
  }

  ngOnInit(): void {
    this.loadCriteria();
    this.loadRemunerations();
    this.loadScoreLevels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCriteria(): void {
    this.loading.show();
    this.financialService.getScoringCriteria()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fullCriteriaList = response.getScoringCriterias.map(item => ({
            id: item.id,
            remunerationId: item.remuneration.id,
            remunerationName: this.capitalizeAndTranslate(item.remuneration.name),
            scoreLevelId: item.scoreLevel.id,
            scoreLevelName: this.capitalizeAndTranslate(item.scoreLevel.levelName),
            minValue: item.minValue,
            maxValue: item.maxValue,
            points: item.points,
          }));
          this.criteriaList = [...this.fullCriteriaList];
          this.loading.hide();
        },
        error: () => {
          this.loading.hide();
          this.showMessage('Atenção', 'Erro ao carregar os critérios.');
        },
      });
  }

  loadRemunerations(): void {
    this.getRemunerationParametersGQL
      .fetch()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.remunerations = response.data.getRemunerationParameters.map((remuneration) => ({
            id: remuneration.id,
            name: this.capitalizeAndTranslate(remuneration.name),
          }));
        },
        error: () => {
          this.showMessage('Erro', 'Erro ao carregar as remunerações.');
        },
      });
  }

  loadScoreLevels(): void {
    this.getScoreLevelsGQL
      .fetch()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.scoreLevels = response.data.getScoreLevels.map((level) => ({
            id: level.id,
            levelName: this.capitalizeAndTranslate(level.levelName),
          }));
        },
        error: () => {
          this.showMessage('Erro', 'Erro ao carregar os níveis de pontuação.');
        },
      });
  }

  search(): void {
    console.log('selectedCriteria.remunerationId:', this.selectedCriteria.remunerationId);
    this.loading.show();
  
    const filteredList = this.fullCriteriaList.filter(criteria => {
      console.log('criteria.remunerationId:', criteria.remunerationId);
      const matchesType = this.selectedCriteria.remunerationId
        ? Number(criteria.remunerationId) === Number(this.selectedCriteria.remunerationId)
        : true;
  
      console.log('matchesType:', matchesType);
  
      const matchesSearchText = this.searchText
        ? (this.containsSearchTerm(criteria.remunerationName) || 
           this.containsSearchTerm(criteria.scoreLevelName))
        : true;
  
      return matchesType && matchesSearchText;
    });
  
    console.log('filteredList:', filteredList);
  
    this.criteriaList = filteredList;
    this.loading.hide(); 
  }

  containsSearchTerm(value: any): boolean {
    if (value == null) return false;

    if (typeof value === 'string') {
      return value.toLowerCase().includes(this.searchText.toLowerCase());
    }
    return false;
  }

  newCriteria(): void {
    this.selectedCriteria = new Criteria();
    this.showModal('modalCriteria');
  }

  editCriteria(criteria: Criteria): void {
    if (criteria && criteria.id) {
      this.selectedCriteria = { ...criteria };
      this.showModal('modalCriteria');
    } else {
      this.showMessage('Erro', 'Critério inválido.');
    }
  }

  saveCriteria(): void {
    if (this.selectedCriteria.id) {
      this.updateCriteria();
    } else {
      this.createCriteria();
    }
  }

  updateCriteria(): void {
    const input = {
      remunerationId: this.selectedCriteria.remunerationId,
      scoreLevelId: this.selectedCriteria.scoreLevelId,
      minValue: Number(this.selectedCriteria.minValue),
      maxValue: Number(this.selectedCriteria.maxValue),
      points: Number(this.selectedCriteria.points),
    };

    const variables = {
      id: this.selectedCriteria.id,
      data: input,
    };

    this.financialService.updateScoringCriteria(variables)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeModal('modalCriteria');
          this.showMessage('Sucesso', 'Critério atualizado com sucesso!', 'success');
          this.refreshCriteriaList();
        },
        error: (error) => {
          this.onError(error);
        },
      });
  }

  createCriteria(): void {
    const variables = {
      input: {
        remunerationId: Number(this.selectedCriteria.remunerationId),
        scoreLevelId: Number(this.selectedCriteria.scoreLevelId),
        minValue: Number(this.selectedCriteria.minValue),
        maxValue: Number(this.selectedCriteria.maxValue),
        points: Number(this.selectedCriteria.points),
      },
    };

    this.financialService.createScoringCriteria(variables)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeModal('modalCriteria');
          this.showMessage('Sucesso', 'Critério criado com sucesso!', 'success');
          this.refreshCriteriaList();
        },
        error: (error) => {
          this.onError(error);
        },
      });
  }

  deleteCriteria(id: number): void {
    if (id) {
      this.financialService.deleteScoringCriteria(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.showMessage('Sucesso', 'Critério deletado com sucesso!', 'success');
            this.refreshCriteriaList();
          },
          error: (error) => {
            this.showMessage('Falha', 'Erro ao deletar critério', 'error');
            console.error('Erro ao excluir critério:', error);
          }
        });
    }
  }

  refreshCriteriaList(): void {
    this.financialService.getScoringCriteria()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response && response.getScoringCriterias) {
            this.criteriaList = response.getScoringCriterias.map(item => ({
              id: item.id,
              remunerationId: item.remuneration.id,
              remunerationName: this.capitalizeAndTranslate(item.remuneration.name),
              scoreLevelId: item.scoreLevel.id,
              scoreLevelName: this.capitalizeAndTranslate(item.scoreLevel.levelName),
              minValue: item.minValue,
              maxValue: item.maxValue,
              points: item.points,
            }));
          } else {
            this.showMessage('Erro', 'Não foi possível carregar os critérios.');
          }
        },
        error: (error) => {
          console.error('Erro ao obter a lista de critérios atualizada:', error);
        }
      });
  }

  capitalizeAndTranslate(value: string): string {
    let translatedValue = value;

    switch (value.toLowerCase()) {
      case 'not attend':
        translatedValue = 'Não Atende';
        break;
      case 'parctially attends':
        translatedValue = 'Atende Parcialmente';
        break;
      case 'attend':
        translatedValue = 'Atende Plenamente';
        break;
      case 'overcomes':
        translatedValue = 'Supera';
        break;
      case 'above':
        translatedValue = 'Acima de';
        break;
      case 'productivity':
        translatedValue = 'Produtivo';
        break;
      case 'financial':
        translatedValue = 'Financeiro';
        break;
      case 'sustainability':
        translatedValue = 'Sustentabilidade';
        break;
      default:
        break;
    }

    return translatedValue
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '); 
  }
}
